import { motion } from "framer-motion";
import { MapPin, Radius, Rocket, BarChart3, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const STEPS = [
  {
    icon: MapPin, num: "STEP 01", title: "Select Your Location",
    text: "Search for any address, venue, stadium, mall, transport hub, business or geographic location using intelligent place search.",
    points: ["Addresses & buildings", "Shopping malls & stadiums", "Railway stations & transport hubs", "Businesses & cities"],
  },
  {
    icon: Radius, num: "STEP 02", title: "Create Your Geofence",
    text: "Select the geographic radius around the location. The geofence preview updates instantly on the interactive map.",
    points: ["100m · 250m · 500m · 750m · 1km", "Custom radius support", "Live visual preview", "Exact coordinates captured"],
  },
  {
    icon: Rocket, num: "STEP 03", title: "Launch Your Campaign",
    text: "Upload your creative, enter campaign details, set your campaign duration and go live.",
    points: ["MP4 video", "JPG & PNG images", "Animated GIF", "Campaign scheduling"],
  },
  {
    icon: BarChart3, num: "STEP 04", title: "Measure Everything",
    text: "Track performance in real time across every location with exportable reports.",
    points: ["Impressions, reach & engagement", "Clicks & video completion", "Location performance rankings", "PDF / CSV reporting"],
  },
];

export default function HowItWorks() {
  return (
    <div>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(29,78,216,0.15),transparent_55%)]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24 relative text-center">
          <p className="section-label">How It Works</p>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl font-bold text-white tracking-tight">
            TURN LOCATIONS INTO <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">OPPORTUNITIES</span>
          </h1>
          <p className="mt-5 text-lg text-slate-400 max-w-2xl mx-auto">
            From location selection to live measurement in four steps.
          </p>
        </div>
      </section>
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 space-y-6">
        {STEPS.map((s, i) => (
          <motion.div key={s.num} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
            className={`card-surface p-8 lg:p-10 grid md:grid-cols-[auto_1fr] gap-7 items-start ${i % 2 === 1 ? "md:ml-12" : "md:mr-12"}`} data-testid={`how-${s.num.replace(/\s/g, "-")}`}>
            <div className="w-14 h-14 rounded-md bg-blue-600/15 border border-blue-500/25 flex items-center justify-center shrink-0">
              <s.icon className="w-7 h-7 text-cyan-400" />
            </div>
            <div>
              <p className="section-label">{s.num}</p>
              <h3 className="mt-2 font-display text-2xl font-bold text-white">{s.title}</h3>
              <p className="mt-3 text-slate-400 leading-relaxed">{s.text}</p>
              <div className="mt-4 grid sm:grid-cols-2 gap-2">
                {s.points.map((p) => (
                  <p key={p} className="text-sm text-slate-300 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-cyan-400 shrink-0" /> {p}
                  </p>
                ))}
              </div>
            </div>
          </motion.div>
        ))}
        <div className="text-center pt-8">
          <Link to="/contact"><Button size="lg" className="bg-blue-600 hover:bg-blue-500 font-semibold px-8" data-testid="how-cta">START YOUR GEOFENCING CAMPAIGN</Button></Link>
        </div>
      </section>
    </div>
  );
}
